import React from 'react'
import Card from '../../components/shared/Card'

/**
 * Disclaimer Page
 * Displays important disclaimers and risk warnings for the investment platform
 */
const DisclaimerPage = () => {
  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Disclaimer
          </h1>
          <p className="text-gray-600 text-lg">
            Last updated: September 11, 2025
          </p>
        </div>

        {/* Content */}
        <Card className="p-6 md:p-8" shadow="medium">
          <div className="prose prose-gray max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Important Notice</h2>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <p className="text-yellow-800 font-medium">
                  <strong>Warning:</strong> This disclaimer contains important information about the risks associated 
                  with investing and using this platform. Please read it carefully before proceeding.
                </p>
              </div>
              <p className="text-gray-700 mb-4">
                By using this investment platform, you acknowledge that you have read, understood, and agree to 
                the terms outlined in this disclaimer.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Investment Risk Disclaimer</h2>
              <p className="text-gray-700 mb-4">
                <strong>High Risk Investment Warning:</strong> Investing in startups, early-stage companies, and 
                private equity involves substantial risk of loss and is not suitable for all investors. You should 
                carefully consider the following risks:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Capital Loss:</strong> You may lose some or all of your invested capital</li>
                <li><strong>Liquidity Risk:</strong> Investments may be illiquid and difficult to sell</li>
                <li><strong>Market Risk:</strong> Investment values can fluctuate significantly</li>
                <li><strong>Company Risk:</strong> Startups may fail or underperform expectations</li>
                <li><strong>Regulatory Risk:</strong> Changes in laws may affect investment value</li>
                <li><strong>Currency Risk:</strong> Foreign investments may be affected by exchange rates</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. No Investment Advice</h2>
              <p className="text-gray-700 mb-4">
                The information provided on this platform is for informational purposes only and does not constitute:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Investment advice or recommendations</li>
                <li>Financial planning or advisory services</li>
                <li>Securities analysis or research reports</li>
                <li>Tax, legal, or accounting advice</li>
                <li>Endorsement of any specific investment opportunity</li>
              </ul>
              <p className="text-gray-700 mt-4">
                You should consult with qualified professionals (financial advisors, attorneys, accountants) 
                before making any investment decisions.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Platform Information Disclaimer</h2>
              <p className="text-gray-700 mb-4">
                While we strive to provide accurate and up-to-date information, we make no representations or 
                warranties regarding:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>The accuracy, completeness, or reliability of any information</li>
                <li>The suitability of any investment opportunity for your specific circumstances</li>
                <li>The future performance of any investment or company</li>
                <li>The availability or terms of any investment opportunity</li>
                <li>The compliance of any company with applicable laws or regulations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Past Performance Disclaimer</h2>
              <p className="text-gray-700 mb-4">
                <strong>Important:</strong> Past performance is not indicative of future results. Historical 
                returns, if shown, are provided for informational purposes only and should not be relied upon 
                as a predictor of future performance. Investment returns can vary significantly and may be negative.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Regulatory and Compliance Disclaimer</h2>
              <p className="text-gray-700 mb-4">
                This platform operates in compliance with applicable securities laws and regulations. However:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Investment opportunities may be subject to regulatory restrictions</li>
                <li>Not all users may be eligible to participate in certain investments</li>
                <li>Compliance requirements may vary by jurisdiction</li>
                <li>Regulatory changes may affect platform operations or investment availability</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Technology and Security Disclaimer</h2>
              <p className="text-gray-700 mb-4">
                While we implement industry-standard security measures, we cannot guarantee:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Complete protection against all security threats or cyber attacks</li>
                <li>Uninterrupted platform availability or performance</li>
                <li>Compatibility with all devices, browsers, or operating systems</li>
                <li>Protection against data loss due to technical failures</li>
                <li>Immediate resolution of all technical issues or bugs</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Third-Party Content Disclaimer</h2>
              <p className="text-gray-700 mb-4">
                Our platform may contain links to third-party websites, content, or services. We:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Do not endorse or verify third-party content</li>
                <li>Are not responsible for third-party websites or services</li>
                <li>Do not guarantee the accuracy of third-party information</li>
                <li>Are not liable for any damages arising from third-party content</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Limitation of Liability</h2>
              <p className="text-gray-700 mb-4">
                To the maximum extent permitted by law, StartupTNT and its affiliates shall not be liable for:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Any investment losses or missed opportunities</li>
                <li>Damages resulting from platform use or unavailability</li>
                <li>Losses due to technical failures or security breaches</li>
                <li>Consequential, indirect, or punitive damages</li>
                <li>Damages exceeding the amount paid for platform services</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. User Responsibility</h2>
              <p className="text-gray-700 mb-4">
                By using this platform, you acknowledge and agree that:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>You are responsible for your own investment decisions</li>
                <li>You understand the risks associated with investing</li>
                <li>You will conduct your own due diligence on investment opportunities</li>
                <li>You will comply with all applicable laws and regulations</li>
                <li>You will not rely solely on platform information for investment decisions</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Changes to Disclaimer</h2>
              <p className="text-gray-700 mb-4">
                We reserve the right to modify this disclaimer at any time. Changes will be effective immediately 
                upon posting. Your continued use of the platform after changes constitutes acceptance of the updated disclaimer.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                If you have questions about this disclaimer or need clarification on any points, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">
                  <strong>Email:</strong> legal@startuptnt.com<br />
                  <strong>Address:</strong> [Your Company Address]<br />
                  <strong>Phone:</strong> [Your Phone Number]
                </p>
              </div>
            </section>

            <div className="bg-red-50 border-l-4 border-red-400 p-4 mt-8">
              <p className="text-red-800 font-medium">
                <strong>Final Warning:</strong> Investing involves risk. Only invest what you can afford to lose. 
                If you are unsure about any aspect of investing, please consult with a qualified financial advisor.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default DisclaimerPage
